<template>
  <div class="navbar-container">
    <div class="title-search-bar-container">
      <div class="title font-weight-black">
        {{ title }}
      </div>
      <div class="search-bar-filter-toggle-container">
        <div class="search-bar-container">
          <v-text-field class="search-bar" v-model="searchQuery" :placeholder="$t('i18n.searchBarPlaceholder')" @input="handleSearchQueryChange" prepend-inner-icon="mdi-magnify" hide-details single-line variant="outlined" density="compact">
          </v-text-field>
        </div>
        <v-btn class="cursor-pointer" variant="text" size="small" icon @click="showFilterToolbar = !showFilterToolbar">
          <v-icon icon="mdi-filter"></v-icon>
          <v-tooltip v-if="!showFilterToolbar" activator="parent" location="start">
            {{ $t('i18n.showFilterToolbar') }}
          </v-tooltip>
        </v-btn>
      </div>
    </div>
    <div v-if="showFilterToolbar" class="toolbar">
      <div class="font-weight-bold">{{ $t('i18n.filters') }}</div>
      <div class=" date-picker-container d-flex justify-center">
        <v-locale-provider locale="pt">
          <v-date-input @click:clear="clearDateRange" prepend-icon="" prepend-inner-icon="$calendar" v-model="selectedDateRange" :label="$t('i18n.datePickerLabel')" :placeholder="$t('i18n.datePickerPlaceholder')" :ok-text="$t('i18n.datePickerOk')" :cancel-text="$t('i18n.datePickerCancel')" min-width="18em" multiple="range" hide-details="auto" variant="outlined" density="compact" color="black" single-line clearable clear-icon="mdi-close-circle" :max="maxDate" @update:model-value="handleSelectedDateRange"></v-date-input>
        </v-locale-provider>
        <v-tooltip activator="parent" location="start">
          {{ $t('i18n.datePicker') }}
        </v-tooltip>
      </div>
      <div class="v-select-container">
        <v-select v-model="selectedDeviceIds" @update:model-value="handleSelectedDeviceIdsChange" :item-props="deviceItemProps" :items="deviceList" clearable prepend-inner-icon="mdi-tablet-cellphone" :placeholder="$t('i18n.tabletLocationPlaceholder')" multiple hide-details="auto" variant="outlined" density="compact">
          <template #selection="{ item, index }">
            <span v-if="index === 0" class="device-selection-text" :title="String(item.title)">{{ item.title }}</span>
            <span v-else-if="index === 1" class="text-caption text-medium-emphasis device-selection-more">
              ({{ additionalSelectedDevicesLabel(selectedDeviceIds.length - 1) }})
            </span>
          </template>
        </v-select>
      </div>
      <div v-if="newPendingFilters" class="action-button">
        <v-btn class="gorate-btn-bg" rounded="xl" variant="flat" @click="handleApplyFiltersClick">{{ $t('i18n.apply') }}</v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useAppStore } from '@/stores/appStore'
import type { DateRange, Device } from '@/utils/DataTypes'

export default defineComponent({
  name: 'DashboardContainerNavbar',
  components: {
    /* UserAvatar */
  },
  props: {
    title: {
      type: String,
      required: true,
      default: 'N/A'
    },
    buttonText: {
      type: String,
      required: false,
      default: 'Button'
    }
  },
  beforeUnmount() {
    this.appStore.setDashboardFilters({
      searchFormQuery: '',
      selectedDateRange: null,
      selectedDeviceIds: []
    })
  },
  setup() {
    const appStore = useAppStore()

    return {
      appStore
    }
  },
  data() {
    return {
      searchQuery: '',
      selectedDateRange: [] as Date[],
      selectedDeviceIds: [] as number[],
      dateRange: null as DateRange | null,
      newPendingFilters: false,
      showFilterToolbar: false
    }
  },
  methods: {
    handleSearchQueryChange() {
      this.$emit('search-input', this.searchQuery)
    },
    handleSelectedDeviceIdsChange() {
      this.newPendingFilters = true
    },
    handleSelectedDateRange() {
      if (Array.isArray(this.selectedDateRange) && this.selectedDateRange.length > 0) {
        const startDate = new Date(this.selectedDateRange[0])
        const endDate = new Date(this.selectedDateRange[this.selectedDateRange.length - 1])
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        this.dateRange = { startDate, endDate }
      } else {
        this.dateRange = null
      }

      this.newPendingFilters = true
      console.log('debug>> selectedDateRange:', this.selectedDateRange)
    },

    deviceItemProps(device: Device) {
      return {
        title: device.name,
        subtitle: device.location,
        value: device.id ?? -1
      }
    },
    additionalSelectedDevicesLabel(count: number) {
      const translationKey = count === 1 ? 'i18n.plusSignXOther' : 'i18n.plusSignXOthersPlural'

      return this.$t(translationKey, { count })
    },
    clearDateRange() {
      this.selectedDateRange = []
      this.dateRange = null
      this.newPendingFilters = true
    },
    handleApplyFiltersClick() {
      this.appStore.setDashboardFilters({
        searchFormQuery: this.searchQuery,
        selectedDateRange: this.dateRange,
        selectedDeviceIds: this.selectedDeviceIds
      })

      this.newPendingFilters = false
    }
  },
  computed: {
    maxDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
    deviceList() {
      return this.appStore.getAppState.deviceList
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.navbar-container {
  grid-row: 1;
  display: grid;
  grid-template-columns: 1fr;
  justify-content: space-between;
  align-items: center;
  background-color: $white;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);

  .title-search-bar-container {
    display: grid;
    grid-template-columns: 1fr min-content;
    align-items: center;
    padding: 0em 1.0em;
    min-height: 4em;

    .navbar-title {
      font-weight: bold;
    }

    .search-bar-filter-toggle-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;

      .search-bar-container {
        display: grid;
        grid-template-columns: 1fr;
        width: 25em;
      }
    }
  }

  .toolbar {
    display: grid;
    grid-template-columns: min-content min-content min-content min-content;
    align-items: center;
    justify-content: flex-end;
    gap: 1em;
    padding: 0 1em 1em 1em;

    .v-select-container {
      width: 20em;
    }

    .device-selection-text {
      max-width: 8em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

@media (max-width: 1023px) {
  .navbar-container {
    .title-search-bar-container {
      grid-template-columns: 1fr;
      gap: 1em;
      padding: 1em 1em;

      .search-bar-filter-toggle-container {
        display: grid;
        gap: 1em;
        justify-content: unset;
        /* border: 1px solid red; */
        width: 100%;

        .search-bar-container {
          /* border: 1px solid red; */
          width: auto;
        }
      }
    }

    .toolbar {
      display: grid;
      grid-template-columns: 1fr;
      align-items: center;
      gap: 0.5em;
      padding: 1em;

      .v-select-container {
        width: 100%;
      }

      .device-selection-text {
        max-width: 8em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
</style>
